import {UseQueryResult, useQuery} from 'react-query';

import {
  ApiObservationTimeSeries,
  ObservationTimeSeriesQuery,
  batchFetchObservationTimeSeries,
} from 'src/lib/api/series';
import {addMilliseconds} from 'src/lib/dates';

export type ObservationTimeSeriesOptions = {
  contextOrgId?: string;
};

export default function useObservationTimeSeries(
  queries: ObservationTimeSeriesQuery[] | null | undefined,
  options: ObservationTimeSeriesOptions = {}
): UseQueryResult<ApiObservationTimeSeries[]> {
  const {contextOrgId} = options;
  return useQuery(
    ['queries', queries, contextOrgId],
    async () => {
      if (!queries || queries.length === 0) {
        return [];
      }

      const queriesWithFCA = queries.filter((q) => q.includeFlowContributingAreas);
      const queriesWithoutFCA = queries.filter((q) => !q.includeFlowContributingAreas);

      const processQueries = (queryList: ObservationTimeSeriesQuery[]) =>
        queryList.map((q) => {
          const query = {...q};
          if (q.endDate) {
            // endDate is exclusive on server side, convert before sending
            query.endDate = addMilliseconds(q.endDate, 1);
          }
          return query;
        });

      const requests: Promise<ApiObservationTimeSeries[]>[] = [];

      if (queriesWithFCA.length > 0) {
        requests.push(
          batchFetchObservationTimeSeries(processQueries(queriesWithFCA), {contextOrgId})
        );
      }
      if (queriesWithoutFCA.length > 0) {
        requests.push(
          batchFetchObservationTimeSeries(processQueries(queriesWithoutFCA), {contextOrgId})
        );
      }

      const results = await Promise.all(requests);
      return results.flat();
    },
    {enabled: !!queries}
  );
}
